import React, { useRef, useEffect } from "react";
import useState from "react-usestateref";
import NeuroCareHeader from "../NeuroCareHeader";
import { patientDb, functions } from "../firebase/firebase";
import { ref, child, get } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../AuthProvider";
import { Layout, Modal, Input } from "antd";
import { AgGridReact } from "ag-grid-react";
import CustomLoadingOverlay from "./CustomLoadingOverlay";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const FIELDS = [
  { key: "code", label: "코드" },
  { key: "name", label: "성함" },
];

const emptyCreateValues = () => {
  const v = {};
  FIELDS.forEach((f) => {
    v[f.key] = "";
  });
  return v;
};

const Surgery = () => {
  const { user, authReady } = useAuth();
  const accountId = String(user?.id ?? "").trim();

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftRows, setDraftRows] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createValues, setCreateValues] = useState(emptyCreateValues());

  const gridApiRef = useRef(null);

  // Account/{로그인한 병원 아이디}/Clients 하위에 날짜시간 키로 환자가 쌓여 있다.
  // 경로 자체가 계정으로 나뉘어 있어 다른 병원 데이터는 조회 대상에 들어오지 않는다.
  const fetchClients = async (id) => {
    try {
      const snap = await get(child(ref(patientDb), `Account/${id}/Clients`));

      const rows = [];
      if (snap.exists()) {
        snap.forEach((c) => {
          const val = c.val() || {};
          rows.push({
            _id: c.key, // 날짜시간 키를 행 식별자로 사용
            code: String(val.code ?? "").trim(),
            name: String(val.name ?? "").trim(),
          });
        });
      }
      // 최근 등록 순
      rows.sort((a, b) => b._id.localeCompare(a._id));

      setRowData(rows);
    } catch (err) {
      console.error("환자 목록 로딩 오류:", err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  // 인증 토큰 복원이 끝난 뒤에 조회해야 권한 거부가 발생하지 않는다.
  useEffect(() => {
    if (!authReady) return;

    if (!accountId) {
      // 로그아웃/계정 전환 시 이전 계정의 목록이 남지 않도록 비운다.
      setRowData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchClients(accountId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, accountId]);

  const gridRows = editMode && draftRows ? draftRows : rowData;

  // 편집 모드/선택이 바뀌면 셀 표시를 갱신 (ag-Grid가 셀을 캐시하므로 강제 필요)
  useEffect(() => {
    gridApiRef.current?.refreshCells({ force: true });
  }, [editMode, selectedRows]);

  const onGridReady = (params) => {
    gridApiRef.current = params.api;
    params.api.sizeColumnsToFit();
  };

  const onSelectionChanged = () => {
    setSelectedRows(gridApiRef.current.getSelectedRows());
  };

  // 편집 모드에서 선택된 행만 수정 가능
  const editableCol = () => {
    const canEdit = (p) => editMode && p.node.isSelected();
    return {
      editable: canEdit,
      cellClassRules: { editableCell: canEdit },
      cellEditor: "agTextCellEditor",
    };
  };

  const columnDefs = [
    { headerName: "NO", valueGetter: (p) => p.node.rowIndex + 1, width: 90, flex: 0 },
    { headerName: "코드", field: "code", ...editableCol() },
    { headerName: "성함", field: "name", ...editableCol() },
  ];

  // 셀 편집 결과는 사본에만 반영 (저장 시 확정)
  const onCellValueChanged = (params) => {
    const id = params.data._id;
    const key = params.colDef.field;
    setDraftRows((prev) =>
      (prev || []).map((row) =>
        row._id === id ? { ...row, [key]: params.newValue } : row
      )
    );
  };

  // 사본과 원본을 비교해 실제로 값이 바뀐 행만 updateClient 함수로 반영한다.
  // (DB 직접 쓰기 대신 함수를 거치면 서버 검증·존재 확인이 일괄 적용된다)
  const saveEdits = async () => {
    const drafts = draftRows || [];
    const changed = drafts.filter((d) => {
      const orig = rowData.find((r) => r._id === d._id);
      return orig && (orig.code !== d.code || orig.name !== d.name);
    });

    // updateClient 함수는 빈 값을 받지 않으므로 저장 전에 걸러서 안내한다.
    const hasEmpty = changed.some(
      (r) => !String(r.code ?? "").trim() || !String(r.name ?? "").trim()
    );
    if (hasEmpty) {
      window.alert("코드와 성함은 비워둘 수 없습니다.");
      const err = new Error("빈 필드가 있어 저장하지 않았습니다.");
      err.handled = true; // 호출부의 공통 오류 알림을 건너뛰기 위한 표시
      throw err;
    }

    const updateClient = httpsCallable(functions, "updateClient");
    await Promise.all(
      changed.map((r) =>
        updateClient({
          clientId: r._id,
          code: String(r.code).trim(),
          name: String(r.name).trim(),
        })
      )
    );

    setRowData(drafts);
    setDraftRows(null);
    setEditMode(false);
    gridApiRef.current?.deselectAll();
    setSelectedRows([]);
  };

  const handleEdit = () => {
    if (editMode) {
      gridApiRef.current?.stopEditing();
      Modal.confirm({
        icon: null,
        title: "수정한 내용을 저장하시겠습니까?",
        okText: "저장",
        cancelText: "취소",
        centered: true,
        // 버튼 로딩상태
        onOk: async () => {
          try {
            await saveEdits();
          } catch (err) {
            if (!err?.handled) {
              console.error("환자 정보 저장 오류:", err);
              window.alert("저장 중 오류가 발생했습니다. 다시 시도해주세요.");
            }
            throw err; // 모달을 닫지 않고 편집 상태를 유지
          }
        },
      });
      return;
    }
    if (selectedRows.length === 0) {
      window.alert("수정할 항목을 선택해주세요.");
      return;
    }
    setDraftRows(rowData.map((row) => ({ ...row })));
    setEditMode(true);
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) {
      window.alert("삭제할 항목을 선택해주세요.");
      return;
    }
    Modal.confirm({
      icon: null,
      title: `선택한 환자 ${selectedRows.length}명을 삭제하시겠습니까?`,
      content: (
        <span style={{ wordBreak: "keep-all" }}>
          해당 환자의 검사데이터와 사진도 함께 삭제되며 되돌릴 수 없습니다.
        </span>
      ),
      okText: "삭제",
      okButtonProps: { danger: true },
      cancelText: "취소",
      centered: true,
      // 버튼 로딩상태
      onOk: async () => {
        // 소프트삭제 적용된 버전 
        // 일단 deleteClientTest 함수를 호출. 나중에 deleteClientTest를 deleteClient로 바꾸면 됨.
        const deleteClient = httpsCallable(functions, "deleteClientTest");
        const targets = [...selectedRows];
        const results = await Promise.allSettled(
          targets.map((r) => deleteClient({ clientId: r._id }))
        );

        // 성공한 행만 목록에서 제거한다. 실패한 행은 남겨서 재시도할 수 있게 한다.
        const deletedIds = new Set(
          targets.filter((_, i) => results[i].status === "fulfilled").map((r) => r._id)
        );
        setRowData((prev) => prev.filter((r) => !deletedIds.has(r._id)));
        gridApiRef.current?.deselectAll();
        setSelectedRows([]);

        const failed = results.filter((res) => res.status === "rejected");
        if (failed.length > 0) {
          console.error("환자 삭제 실패:", failed.map((f) => f.reason));
          window.alert(`${failed.length}건 삭제에 실패했습니다. 다시 시도해주세요.`);
        }
      },
    });
  };

  const handleRegister = () => {
    setCreateValues(emptyCreateValues());
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setCreateValues(emptyCreateValues());
  };

  const handleCreateSave = async () => {
    const trimmed = {};
    FIELDS.forEach((f) => {
      trimmed[f.key] = String(createValues[f.key] ?? "").trim();
    });

    // registerClient 함수는 code와 name을 모두 요구한다 (앱 등록과 동일한 기준).
    if (!trimmed.code || !trimmed.name) {
      window.alert("코드와 성함을 모두 입력해주세요.");
      return;
    }
    if (!accountId) {
      window.alert("로그인 정보가 없어 등록할 수 없습니다.");
      return;
    }
    if (saving) return;

    setSaving(true);
    try {
      // 일단 중복code방지로 registerClientTest를 호출.
      // 나중에 registerClientTest를 registerClient로 바꾸면 됨.
      const registerClient = httpsCallable(functions, "registerClientTest");
      const res = await registerClient({
        code: trimmed.code,
        name: trimmed.name,
      });
      const clientId = res.data?.clientId;

      setRowData((prev) => [
        { _id: clientId, ...trimmed },
        ...prev.filter((r) => r._id !== clientId),
      ]);
      closeCreateModal();
    } catch (err) {
      console.error("환자 등록 오류:", err);
      // 서버가 보낸 거부 사유(중복 코드 등)가 있으면 그대로 보여준다.
      window.alert(
        err?.message || "등록 중 오류가 발생했습니다. 다시 시도해주세요."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout style={{ background: "#ffffff" }}>
      <NeuroCareHeader selectedMenu={"환자 등록"} />
      <div className="surgeryContainer">
        <div className="panel surgeryPanel">
          <div className="gridWrap">
            <div
              className="ag-theme-alpine"
              style={{ height: "100%", width: "100%" }}
            >
              <AgGridReact
                onGridReady={onGridReady}
                rowData={gridRows}
                getRowId={(p) => String(p.data._id)}
                columnDefs={columnDefs}
                defaultColDef={{ resizable: true, filter: false, flex: 1 }}
                loadingOverlayComponent={CustomLoadingOverlay}
                loadingOverlayComponentParams={{
                  loadingMessage: "로딩 중입니다...",
                }}
                loading={loading}
                rowSelection={{ mode: "multiRow" }}
                onSelectionChanged={onSelectionChanged}
                onCellValueChanged={onCellValueChanged}
                singleClickEdit={true}
                stopEditingWhenCellsLoseFocus={true}
                overlayNoRowsTemplate={
                  '<span style="padding: 10px; color: #888;">데이터가 없습니다.</span>'
                }
              />
            </div>
          </div>

          <div className="footerBar surgeryFooter">
            {!editMode && (
              <button
                className="surgeryBtn surgeryBtnDanger"
                onClick={handleDelete}
              >
                삭제
              </button>
            )}
            <button
              className={`surgeryBtn ${
                editMode ? "surgeryBtnPrimary" : "surgeryBtnGhost"
              }`}
              onClick={handleEdit}
            >
              {editMode ? "수정 완료" : "수정"}
            </button>
            {!editMode && (
              <button
                className="surgeryBtn surgeryBtnPrimary"
                onClick={handleRegister}
              >
                등록
              </button>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={createModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        width={520}
        centered
        closeIcon={false}
        styles={{ body: { padding: "10px 4px" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div
            style={{ fontSize: "18px", fontWeight: "bold", color: "#1E293B" }}
          >
            환자 등록
          </div>

          <div className="surgeryEditForm">
            {FIELDS.map((field) => (
              <div className="surgeryEditRow" key={field.key}>
                <div className="surgeryEditLabel">{field.label}</div>
                <Input
                  className="surgeryEditSelect"
                  value={createValues[field.key]}
                  placeholder="입력"
                  onChange={(e) =>
                    setCreateValues((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                />
              </div>
            ))}
          </div>

          <div className="surgeryFooter surgeryModalFooter">
            <button
              className="surgeryBtn surgeryBtnGhost"
              onClick={closeCreateModal}
            >
              취소
            </button>
            <button
              className="surgeryBtn surgeryBtnPrimary"
              onClick={handleCreateSave}
              disabled={saving}
            >
              {saving ? "등록 중..." : "등록"}
            </button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};
export default Surgery;
